import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { iaApi } from '../services/api';
import { SIMULATED_PENDING_PHASE_KEYS } from '../constants/iaJobPhases';
import { normalizeJobResultPayload } from '../utils/iaJobResult';

const PENDING_PHASE_INTERVAL_MS = 10000;

const STORAGE_KEY = 'iron_ia_job_watch';

const IaJobsContext = createContext(null);

function readStoredWatchIds() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeStoredWatchIds(ids) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...new Set(ids)]));
  } catch {
    /* ignore */
  }
}

function jobLabel(job) {
  if (job.contextLabel) return job.contextLabel;
  if (job.type === 'onboarding_pack') return 'Treinos sugeridos';
  if (job.type === 'duo_pack') return 'Treino duo';
  if (job.type === 'set_feedback') return 'Análise do set';
  if (job.type === 'exercise_complete') return 'Resumo do exercício';
  if (job.type === 'session_complete') return 'Resumo da sessão';
  return 'Processamento IA';
}

export function IaJobsProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [onboardingReview, setOnboardingReviewState] = useState(null);
  const [feedbackReview, setFeedbackReviewState] = useState(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const callbacksRef = useRef(new Map());
  const pollWarnAtRef = useRef(0);
  const jobsRef = useRef([]);
  const onboardingReviewOpenRef = useRef(null);
  const feedbackReviewOpenRef = useRef(null);

  jobsRef.current = jobs;

  const shouldPollFast = useCallback(() => {
    if (callbacksRef.current.size > 0) return true;
    if (readStoredWatchIds().length > 0) return true;
    return (jobsRef.current || []).length > 0;
  }, []);

  const registerJobCallback = useCallback((jobId, handlers = {}) => {
    if (!jobId) return () => {};
    callbacksRef.current.set(String(jobId), handlers);
    writeStoredWatchIds([...callbacksRef.current.keys(), ...readStoredWatchIds()]);
    return () => {
      callbacksRef.current.delete(String(jobId));
    };
  }, []);

  const pollOnce = useCallback(async () => {
    try {
      const res = await iaApi.getActiveIaJobs();
      const active = Array.isArray(res.data?.jobs) ? res.data.jobs : [];
      setJobs(active);

      const watched = readStoredWatchIds();
      for (const id of watched) {
        const inActive = active.find((j) => String(j.id) === String(id));
        if (inActive) continue;
        try {
          const detail = await iaApi.getIaJob(id);
          const job = detail.data;
          if (!job) continue;
          const handlers = callbacksRef.current.get(String(id));
          if (job.status === 'done' && handlers?.onDone) {
            handlers.onDone(normalizeJobResultPayload(job.resultPayload), job);
            callbacksRef.current.delete(String(id));
          } else if (job.status === 'failed' && handlers?.onFailed) {
            handlers.onFailed(job.errorMessage, job);
            callbacksRef.current.delete(String(id));
          } else if (job.status === 'cancelled') {
            callbacksRef.current.delete(String(id));
          }
        } catch {
          /* job may be gone */
        }
      }

      for (const job of active) {
        const handlers = callbacksRef.current.get(String(job.id));
        if (handlers?.onUpdate) handlers.onUpdate(job);
        if (job.status === 'done' && handlers?.onDone) {
          handlers.onDone(normalizeJobResultPayload(job.resultPayload), job);
          callbacksRef.current.delete(String(job.id));
        } else if (job.status === 'failed' && handlers?.onFailed) {
          handlers.onFailed(job.errorMessage, job);
          callbacksRef.current.delete(String(job.id));
        }
      }

      writeStoredWatchIds([...callbacksRef.current.keys()]);
    } catch (e) {
      const now = Date.now();
      if (now - pollWarnAtRef.current > 10000) {
        pollWarnAtRef.current = now;
        console.warn('[IaJobsProvider] poll falhou:', e?.message || e);
      }
    }
  }, []);

  useEffect(() => {
    pollOnce();
    let fastId = null;
    const reconcile = () => {
      if (shouldPollFast()) {
        if (!fastId) fastId = setInterval(pollOnce, 2000);
      } else if (fastId) {
        clearInterval(fastId);
        fastId = null;
      }
    };
    reconcile();
    const reconcileId = setInterval(reconcile, 3000);
    const idleId = setInterval(pollOnce, 30000);
    return () => {
      if (fastId) clearInterval(fastId);
      clearInterval(reconcileId);
      clearInterval(idleId);
    };
  }, [pollOnce, shouldPollFast]);

  const setOnboardingReview = useCallback((review) => {
    if (review == null || !Number.isFinite(review.count) || review.count < 1) {
      setOnboardingReviewState(null);
      return;
    }
    setOnboardingReviewState({ count: Math.floor(review.count) });
  }, []);

  const clearOnboardingReview = useCallback(() => {
    setOnboardingReviewState(null);
  }, []);

  const registerOnboardingReviewOpen = useCallback((handler) => {
    onboardingReviewOpenRef.current = typeof handler === 'function' ? handler : null;
    return () => {
      if (onboardingReviewOpenRef.current === handler) {
        onboardingReviewOpenRef.current = null;
      }
    };
  }, []);

  const openOnboardingReview = useCallback(() => {
    onboardingReviewOpenRef.current?.();
  }, []);

  const setFeedbackReview = useCallback((review) => {
    if (!review?.markdown?.trim()) {
      setFeedbackReviewState(null);
      return;
    }
    setFeedbackReviewState(review);
  }, []);

  const clearFeedbackReview = useCallback(() => {
    setFeedbackReviewState(null);
    setFeedbackModalOpen(false);
  }, []);

  const registerFeedbackReviewOpen = useCallback((handler) => {
    feedbackReviewOpenRef.current = typeof handler === 'function' ? handler : null;
    return () => {
      if (feedbackReviewOpenRef.current === handler) {
        feedbackReviewOpenRef.current = null;
      }
    };
  }, []);

  const openFeedbackReview = useCallback(() => {
    setFeedbackModalOpen(true);
    feedbackReviewOpenRef.current?.();
  }, []);

  const trackPending = useCallback(({ id, label, engineLabel = 'IA' }) => {
    if (!id) return;
    const entry = {
      id: String(id),
      label: label || 'Processamento IA',
      engineLabel,
      phase: 'recebidos',
      startedAt: Date.now(),
    };
    setPendingRequests((prev) => [...prev.filter((p) => p.id !== entry.id), entry]);
  }, []);

  const clearPending = useCallback((id) => {
    if (!id) return;
    setPendingRequests((prev) => prev.filter((p) => p.id !== String(id)));
  }, []);

  const updatePending = useCallback((id, partial) => {
    if (!id) return;
    setPendingRequests((prev) =>
      prev.map((p) => (p.id === String(id) ? { ...p, ...partial } : p))
    );
  }, []);

  useEffect(() => {
    if (pendingRequests.length === 0) return undefined;
    const timerId = setInterval(() => {
      setPendingRequests((prev) =>
        prev.map((p) => {
          const idx = SIMULATED_PENDING_PHASE_KEYS.indexOf(p.phase || 'recebidos');
          if (idx < 0 || idx >= SIMULATED_PENDING_PHASE_KEYS.length - 1) return p;
          return { ...p, phase: SIMULATED_PENDING_PHASE_KEYS[idx + 1] };
        })
      );
    }, PENDING_PHASE_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, [pendingRequests.length]);

  const hasIaBarActivity = useMemo(() => {
    const hasReview =
      (onboardingReview != null && onboardingReview.count > 0) ||
      (feedbackReview != null && feedbackReview.markdown?.trim());
    return jobs.length > 0 || pendingRequests.length > 0 || hasReview;
  }, [jobs.length, pendingRequests.length, onboardingReview, feedbackReview]);

  const value = useMemo(
    () => ({
      jobs,
      pendingRequests,
      trackPending,
      clearPending,
      updatePending,
      hasIaBarActivity,
      onboardingReview,
      feedbackReview,
      feedbackModalOpen,
      setFeedbackModalOpen,
      registerJobCallback,
      refreshJobs: pollOnce,
      jobLabel,
      setOnboardingReview,
      clearOnboardingReview,
      registerOnboardingReviewOpen,
      openOnboardingReview,
      setFeedbackReview,
      clearFeedbackReview,
      registerFeedbackReviewOpen,
      openFeedbackReview,
    }),
    [
      jobs,
      pendingRequests,
      trackPending,
      clearPending,
      updatePending,
      hasIaBarActivity,
      onboardingReview,
      feedbackReview,
      feedbackModalOpen,
      registerJobCallback,
      pollOnce,
      setOnboardingReview,
      clearOnboardingReview,
      registerOnboardingReviewOpen,
      openOnboardingReview,
      setFeedbackReview,
      clearFeedbackReview,
      registerFeedbackReviewOpen,
      openFeedbackReview,
    ]
  );

  return <IaJobsContext.Provider value={value}>{children}</IaJobsContext.Provider>;
}

export function useIaJobs() {
  const ctx = useContext(IaJobsContext);
  if (!ctx) {
    throw new Error('useIaJobs must be used within IaJobsProvider');
  }
  return ctx;
}
